/**
 * Client side of the agreement-extraction seam.
 *
 * The envelope mirrors the API's `ExtractionResult` exactly, and the merge below
 * is a straight key-for-key copy into form state — no translation table. That is
 * only possible because the server keys `fields` by the SAME body keys the
 * create endpoint accepts, and it is worth keeping that way: a mapping layer
 * here would be a second place to forget a field, and a forgotten field looks
 * identical to one the document did not contain.
 */

/** Kept in sync with `FieldSource` in the API's agreement-extraction.ts. */
export type FieldSource = 'btp-export' | 'delimited' | 'llm';

export interface ExtractedField {
  value: string;
  source: FieldSource;
  /** 0..1. Below FULL_CONFIDENCE the adapter guessed and said so in `evidence`. */
  confidence: number;
  evidence: string;
}

export interface ExtractionResult {
  fields: Record<string, ExtractedField>;
  hints: Record<string, ExtractedField>;
  adapter: string;
  unmapped: string[];
  warnings: string[];
  note?: string;
}

export interface ExtractResponse {
  blob_path: string;
  extraction: ExtractionResult;
}

/**
 * The blob path out of a FAILED extract, when the document was saved anyway.
 *
 * `extraction_failed` means the upload succeeded and only the reading failed, so the
 * banner tells the reviewer to fill the form in by hand because "the document is
 * already attached". Until 2026-08-14 that sentence was false in both directions: the
 * server did not send `blob_path` on this arm, and then — after it did — the client
 * still read only `err.message`. So the reviewer reached the manual form with no
 * attachment, the create submit omitted `contract_blob_path`, and the extract-time
 * blob orphaned. Same finding twice, one layer apart.
 *
 * Parsing it HERE, beside `ExtractResponse`, because this is the client's half of that
 * endpoint's contract and the failure arm is as much a part of it as the success arm.
 * A component reading `err.body` by hand is how the two drift.
 *
 * Deliberately narrow. It returns a path ONLY for `extraction_failed` — the arm where
 * the document is known to be stored. `storage_unavailable` means nothing was saved,
 * and inventing a path there would attach an agreement to a blob that does not exist.
 */
export function savedBlobPathFromFailure(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) return null;
  const b = body as { kind?: unknown; blob_path?: unknown };
  if (b.kind !== 'extraction_failed') return null;
  return typeof b.blob_path === 'string' && b.blob_path.trim() !== ''
    ? b.blob_path
    : null;
}

export const FULL_CONFIDENCE = 1;

/**
 * A value the adapter was not sure about.
 *
 * Split out as a named predicate rather than inlined at each call site because
 * it decides what gets visually flagged for review, and "which values did we ask
 * a human to look at" should have exactly one definition.
 */
export function needsReview(f: ExtractedField): boolean {
  return f.confidence < FULL_CONFIDENCE;
}

/**
 * Merge extracted values into form state.
 *
 * Only keys the form ALREADY has are applied. The envelope can legitimately
 * carry keys this form does not render — `customer_id` and `property_id` are
 * resolved by the server and drive the two pickers, not a text input — and
 * blindly spreading would add junk keys to the submitted body that the API would
 * then reject as a whole.
 *
 * Returns the derived map separately from the form so the two never get confused:
 * form state is what will be submitted, `derived` is only provenance.
 */
export function applyExtraction<T extends Record<string, string>>(
  form: T,
  fields: Record<string, ExtractedField>,
): { form: T; derived: Record<string, ExtractedField> } {
  const next = { ...form };
  const derived: Record<string, ExtractedField> = {};

  for (const [key, field] of Object.entries(fields)) {
    if (!(key in next)) continue;
    // An empty extracted value is not an improvement on whatever is there.
    if (field.value.trim() === '') continue;
    (next as Record<string, string>)[key] = field.value;
    derived[key] = field;
  }
  return { form: next, derived };
}

/**
 * Upload a document and get back a proposal.
 *
 * The filename rides in a HEADER rather than a query parameter deliberately: a
 * contract's filename routinely contains the client's name, and query strings
 * end up in access logs and browser history in a way headers do not.
 */
export async function extractFromDocument(
  file: File,
  apiFetch: <T>(url: string, init?: RequestInit) => Promise<T>,
): Promise<ExtractResponse> {
  const bytes = await file.arrayBuffer();
  return apiFetch<ExtractResponse>('/api/btp/agreements/extract', {
    method: 'POST',
    body: bytes,
    headers: {
      // Non-ASCII in a header value is not transmissible; the server only uses
      // this to build a storage path, so degrading to a safe name beats failing
      // the upload of a file called "Café Rate Agreement.pdf".
      'x-upload-filename': file.name.replace(/[^\x20-\x7E]/g, '_'),
      'Content-Type': file.type || 'application/octet-stream',
    },
  });
}

/**
 * How many proposed values a reviewer is being handed.
 *
 * Counts hints too — a resolved client is one of the most consequential things
 * the extractor proposes, and a count that excluded it would understate what
 * needs checking.
 */
export function proposedCount(e: ExtractionResult): number {
  return Object.keys(e.fields).length;
}
